import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { HubConnectionBuilder, HubConnection } from '@microsoft/signalr';
import config from '../../_config/config.const';
import { accountService } from '../../_services/accounts.service';

const SignalRContext = createContext<any>(null);

export function SignalRProvider({ children }: { children: React.ReactNode }) {
    const [connection, setConnection] = useState<HubConnection | null>(null);
    const [onlineUsers, setOnlineUsers] = useState<string[]>([]);
    const [incomingMessages, setIncomingMessages] = useState<{ fromUserId: string, toUserId: any, message: string, groupId?: any, timestamp?: any }[]>([]);
    const [currentUserId, setCurrentUserId] = useState(accountService.currentUserValue?.id);

    //init audio notification
    const notifyRef = useRef<HTMLAudioElement | null>(null);
    useEffect(() => {
        notifyRef.current = new Audio('/audio/chat_notification.mp3');
        if (notifyRef.current) {
            notifyRef.current.preload = 'auto';
          
        }
    }, []);

    useEffect(() => {
        const sub = accountService.currentUser?.subscribe(user => {
            setCurrentUserId(user?.id)
        });
        return () => sub?.unsubscribe?.();
    }, []);

    useEffect(() => {
        const conn = new HubConnectionBuilder()
            .withUrl(`${config.CHAT}?userId=${currentUserId}`)
            .withAutomaticReconnect()
            .build();

        conn.start().then(() => {
            if (!currentUserId) return;

            console.log("SignalR connected");
            conn.invoke('UserConnected', currentUserId);

            conn.on('UpdateUserList', (ids: string[]) => setOnlineUsers(ids));
            conn.on('UserOnline', id => setOnlineUsers(_user => [..._user, id]));
            conn.on('UserOffline', id => setOnlineUsers(_user => _user.filter(i => i !== id)));

            //1:1 msg
            conn.on('ReceiveMessage', (fromUserId, toUserId, message, timestamp) => {
                if (fromUserId !== currentUserId) {
                    setIncomingMessages(_msg => [..._msg, { fromUserId, toUserId, message, timestamp }]);
                    notifyRef.current?.play().catch(() => { });
                }
            });

            //Group msg
            conn.on('ReceiveGroupMessage', (groupId, fromUserId, message, timestamp) => {
                if (fromUserId !== currentUserId) {
                    setIncomingMessages(_msg => [..._msg, { fromUserId, toUserId: null, message, groupId, timestamp }]);
                    notifyRef.current?.play().catch(() => { });
                }
            });
        });

        setConnection(conn);

        return () => {
            conn.stop();
        };
    }, [currentUserId]);

    useEffect(() => {
        if (!connection) return;

        const handler = () => {
            console.log("ChatGroupsChanged event received");
        };

        connection.on('ChatGroupsChanged', handler);

        return () => {
            connection.off('ChatGroupsChanged', handler);
        };
    }, [connection]);

    return (
        <SignalRContext.Provider value={{ connection, onlineUsers, incomingMessages, setIncomingMessages }}>
            {children}
        </SignalRContext.Provider>
    );
}

export function useSignalR() {
    return useContext(SignalRContext);
}
